import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  ClipboardX,
  FileText,
  Package,
  PiggyBank,
  Printer,
  Receipt,
  RotateCcw,
  Search,
  Users,
  Wallet,
  XCircle,
} from "lucide-react";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { KpiCard, KpiGrid } from "../components/KpiCard";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { fetchAdminReporting, type AdminReporting, type ReportingFilters, type SpendingStats } from "../api/reportingApi";
import { ExportMenu } from "../components/ExportMenu";

function money(value: number): string {
  return `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
}

function number(value: number): string {
  return value.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
}

const today = new Date().toISOString().slice(0, 10);
const DEFAULT_FILTERS: ReportingFilters = { from: `${today.slice(0, 4)}-01-01`, to: today, insured: "family" };

const INSURED_LABELS: Record<NonNullable<ReportingFilters["insured"]>, string> = {
  family: "Famille",
  agent: "Agents",
  spouse: "Conjoints",
  child: "Enfants",
};

function StatsCells({ row }: { row: SpendingStats }) {
  return (
    <>
      <td className="px-3 py-2 text-right tabular-nums">{number(row.acts)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{money(row.average)}</td>
      <td className="px-3 py-2 text-right font-semibold tabular-nums">{money(row.total)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.ratio.toLocaleString("fr-FR")} %</td>
    </>
  );
}

const STAT_HEADS = ["Nbre d'actes", "Consom. moyenne", "Consom. totale", "Ratio"];

function Table({ title, heads, children, empty }: { title: string; heads: string[]; children: React.ReactNode; empty: boolean }) {
  return (
    <section className="theme-card p-5">
      <h2 className="mb-3 text-base font-semibold text-foreground">{title}</h2>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-card-muted text-left text-xs uppercase text-muted">
            <tr>
              {heads.map((head, index) => (
                <th key={head} className={`px-3 py-2 ${index >= heads.length - 4 ? "text-right" : ""}`}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {empty ? (
              <tr>
                <td colSpan={heads.length} className="px-3 py-4 text-center text-muted">Aucune dépense validée sur ces filtres.</td>
              </tr>
            ) : (
              children
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Reporting Admin (cahier des charges, lot 4) : filtres (période, assureur, prestataire, entreprise, lot de factures),
 * données clés, puis dépenses par nature d'actes (détail par code acte et prestataire), par prestataire,
 * par pathologie et par assuré (famille, agents, conjoints, enfants).
 */
export function AdminReportingPage() {
  const { exportTable, printPage } = usePageActions();
  const [draft, setDraft] = useState<ReportingFilters>(DEFAULT_FILTERS);
  const [applied, setApplied] = useState<ReportingFilters>(DEFAULT_FILTERS);
  const [report, setReport] = useState<AdminReporting | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openNature, setOpenNature] = useState<string | null>(null);
  const [showAllDetail, setShowAllDetail] = useState(false);

  const load = useCallback(async (filters: ReportingFilters) => {
    setLoading(true);
    setError(null);
    try {
      setReport(await fetchAdminReporting(filters));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reporting indisponible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(applied);
  }, [applied, load]);

  const companies = useMemo(
    () => (report?.options.companies ?? []).filter((c) => !draft.insurer_id || c.insurer_id === draft.insurer_id),
    [report, draft.insurer_id],
  );

  const key = report?.key_figures;
  const detailFor = (natureKey: string) => (report?.by_nature_detail ?? []).filter((row) => row.key === natureKey);

  const exportAll = () => {
    if (!report) return;
    exportTable(
      `reporting-${applied.from}-${applied.to}.csv`,
      ["Tableau", "Libellé", "Détail", "Nbre d'actes", "Consom. moyenne", "Consom. totale", "Ratio %"],
      [
        ...report.by_nature.map((r) => ["Nature d'actes", r.nature, r.codes.join(" "), String(r.acts), String(r.average), String(r.total), String(r.ratio)]),
        ...report.by_nature_detail.map((r) => ["Nature / code / prestataire", r.nature, `${r.code} · ${r.provider}`, String(r.acts), String(r.average), String(r.total), String(r.ratio)]),
        ...report.by_provider.map((r) => ["Prestataire", r.name, "", String(r.acts), String(r.average), String(r.total), String(r.ratio)]),
        ...report.by_pathology.map((r) => ["Pathologie", r.label, r.code ?? "", String(r.acts), String(r.average), String(r.total), String(r.ratio)]),
        ...report.by_insured.map((r) => ["Assuré", r.patient, `${r.family_code} · ${r.insured}`, String(r.acts), String(r.average), String(r.total), String(r.ratio)]),
      ],
    );
  };

  const setId = (field: "insurer_id" | "hospital_id" | "company_id", value: string) =>
    setDraft((d) => ({ ...d, [field]: value ? Number(value) : null, ...(field === "insurer_id" ? { company_id: null } : {}) }));

  return (
    <>
      <Breadcrumbs items={[{ label: "Reporting" }]} />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-foreground">Reporting</h1>
        <div className="flex gap-2">
          {report ? <ExportMenu onExport={exportAll} /> : null}
          <button type="button" onClick={printPage} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent/20 hover:border-accent">
            <Printer size={14} />
            Imprimer
          </button>
        </div>
      </div>

      <section className="header-ribbon filter-panel relative isolate rounded-[var(--radius-card)] mb-6 p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <FormField label="Période du">
            <input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className={inputClass} />
          </FormField>
          <FormField label="Au">
            <input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className={inputClass} />
          </FormField>
          <FormField label="Assureur">
            <select value={draft.insurer_id ?? ""} onChange={(e) => setId("insurer_id", e.target.value)} className={selectClass}>
              <option value="">Tous les assureurs</option>
              {(report?.options.insurers ?? []).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Prestataire">
            <select value={draft.hospital_id ?? ""} onChange={(e) => setId("hospital_id", e.target.value)} className={selectClass}>
              <option value="">Tous les prestataires</option>
              {(report?.options.hospitals ?? []).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Entreprise">
            <select value={draft.company_id ?? ""} onChange={(e) => setId("company_id", e.target.value)} className={selectClass}>
              <option value="">Toutes les entreprises</option>
              {companies.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Lot de factures">
            <input list="reporting-batches" value={draft.batch ?? ""} onChange={(e) => setDraft({ ...draft, batch: e.target.value || null })} className={inputClass} placeholder="LOT-…" />
            <datalist id="reporting-batches">
              {(report?.options.batches ?? []).map((batch) => (
                <option key={batch} value={batch} />
              ))}
            </datalist>
          </FormField>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="button" disabled={loading || !draft.from || !draft.to} onClick={() => setApplied({ ...draft })} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60">
            <Search size={15} />
            Appliquer
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(DEFAULT_FILTERS);
              setApplied(DEFAULT_FILTERS);
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent/20 hover:border-accent"
          >
            <RotateCcw size={14} />
            Réinitialiser
          </button>
        </div>
      </section>

      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}
      {loading && !report ? <p className="text-sm text-muted">Chargement…</p> : null}

      {report && key ? (
        <div className="flex flex-col gap-6">
          <section>
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-semibold text-foreground">a) Données clés</h2>
              <span className="text-xs text-muted">Extraction du {new Date(key.extraction_date.replace(" ", "T")).toLocaleString("fr-FR")}</span>
            </div>
            <KpiGrid className="grid-cols-1 md:grid-cols-2">
              <KpiCard
                tone="sky"
                icon={Users}
                watermark={Users}
                label="Nombre d'assurés"
                value={number(key.insured.total)}
                hint={`dont agents ${number(key.insured.agents)} · conjoint(e)s ${number(key.insured.spouses)} · enfants ${number(key.insured.children)}`}
              />
              <KpiCard
                tone="mint"
                icon={Wallet}
                watermark={Wallet}
                label="Dépenses"
                value={money(key.spending.total)}
                hint={`dont agents ${money(key.spending.agents)} · conjoint(e)s ${money(key.spending.spouses)} · enfants ${money(key.spending.children)}`}
              />
            </KpiGrid>
            <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground">Factures reçues pour validation</h3>
            <KpiGrid className="grid-cols-1 sm:grid-cols-3">
              <KpiCard tone="sky" icon={FileText} label="Reçues" value={number(key.invoices.received)} />
              <KpiCard tone="mint" icon={CheckCircle} label="Validées" value={number(key.invoices.validated)} />
              <KpiCard tone="red" icon={XCircle} label="Rejetées" value={number(key.invoices.rejected)} />
              <KpiCard tone="purple" icon={Receipt} label="Montant total facturé" value={money(key.amounts.billed)} />
              <KpiCard tone="amber" icon={PiggyBank} label="Montant total réfacté" value={money(key.amounts.refacted)} />
              <KpiCard tone="lime" icon={BadgeCheck} label="Montant total validé" value={money(key.amounts.validated)} />
            </KpiGrid>
            <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground">Demandes de validation</h3>
            <KpiGrid className="grid-cols-1 sm:grid-cols-3">
              <KpiCard tone="sky" icon={ClipboardList} label="Reçues" value={number(key.requests.received)} />
              <KpiCard tone="mint" icon={ClipboardCheck} label="Validées" value={number(key.requests.validated)} />
              <KpiCard tone="red" icon={ClipboardX} label="Rejetées" value={number(key.requests.rejected)} />
            </KpiGrid>
            <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground">Dépenses par package</h3>
            {key.by_package.length === 0 ? <p className="text-sm text-muted">Aucune dépense validée.</p> : null}
            <KpiGrid className="grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
              {key.by_package.map((pkg) => (
                <KpiCard key={pkg.name} tone="purple" icon={Package} label={pkg.name} value={money(pkg.amount)} />
              ))}
            </KpiGrid>
          </section>

          <Table title="b) Dépenses par nature d'actes" heads={["#", "Nature", "Code acte", ...STAT_HEADS]} empty={report.by_nature.length === 0}>
            {report.by_nature.map((row, index) => {
              const open = openNature === row.key;
              return (
                <Fragment key={row.key}>
                  <tr className="cursor-pointer border-t border-border hover:bg-card-muted/40" onClick={() => setOpenNature(open ? null : row.key)}>
                    <td className="px-3 py-2 text-muted">{index + 1}</td>
                    <td className="px-3 py-2 font-medium text-primary">
                      <span className="inline-flex items-center gap-1">
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {row.nature}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{row.codes.join(", ")}</td>
                    <StatsCells row={row} />
                  </tr>
                  {open
                    ? detailFor(row.key).map((detail) => (
                        <tr key={`${detail.code}-${detail.provider}`} className="border-t border-border bg-card-muted/30 text-xs">
                          <td />
                          <td className="px-3 py-1.5 text-muted">{detail.provider}</td>
                          <td className="px-3 py-1.5 font-mono">{detail.code}</td>
                          <StatsCells row={detail} />
                        </tr>
                      ))
                    : null}
                </Fragment>
              );
            })}
          </Table>
          <div className="-mt-4">
            <button type="button" onClick={() => setShowAllDetail((v) => !v)} className="text-sm text-primary hover:underline">
              {showAllDetail ? "− Masquer le détail" : "+ Voir plus : détail par code acte et prestataire"}
            </button>
          </div>
          {showAllDetail ? (
            <Table title="Détail par nature, code acte et prestataire" heads={["#", "Nature", "Code acte", "Prestataire", ...STAT_HEADS]} empty={report.by_nature_detail.length === 0}>
              {report.by_nature_detail.map((row, index) => (
                <tr key={`${row.key}-${row.code}-${row.provider}`} className="border-t border-border">
                  <td className="px-3 py-2 text-muted">{index + 1}</td>
                  <td className="px-3 py-2">{row.nature}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                  <td className="px-3 py-2">{row.provider}</td>
                  <StatsCells row={row} />
                </tr>
              ))}
            </Table>
          ) : null}

          <Table title="c) Dépenses par prestataire" heads={["#", "Prestataire", ...STAT_HEADS]} empty={report.by_provider.length === 0}>
            {report.by_provider.map((row, index) => (
              <tr key={row.name} className="border-t border-border">
                <td className="px-3 py-2 text-muted">{index + 1}</td>
                <td className="px-3 py-2 font-medium">{row.name}</td>
                <StatsCells row={row} />
              </tr>
            ))}
          </Table>

          <Table title="d) Dépenses par pathologie" heads={["#", "Code affection", "Nom pathologie", ...STAT_HEADS]} empty={report.by_pathology.length === 0}>
            {report.by_pathology.map((row, index) => (
              <tr key={row.code ?? "none"} className="border-t border-border">
                <td className="px-3 py-2 text-muted">{index + 1}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.code ?? "—"}</td>
                <td className="px-3 py-2">{row.label}</td>
                <StatsCells row={row} />
              </tr>
            ))}
          </Table>

          <section className="theme-card p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-foreground">e) Dépenses par assuré</h2>
              <div className="flex flex-wrap gap-1" role="group" aria-label="Filtre des assurés">
                {(Object.keys(INSURED_LABELS) as Array<keyof typeof INSURED_LABELS>).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      setDraft((d) => ({ ...d, insured: mode }));
                      setApplied((a) => ({ ...a, insured: mode }));
                    }}
                    className={`rounded-lg px-3 py-1.5 text-sm ${applied.insured === mode ? "bg-primary text-white" : "border border-border text-foreground hover:bg-accent/20 hover:border-accent"}`}
                  >
                    {INSURED_LABELS[mode]}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                  <tr>
                    {["#", "Code SFC", "Nom patient", "Nom assuré"].map((head) => (
                      <th key={head} className="px-3 py-2">{head}</th>
                    ))}
                    {STAT_HEADS.map((head) => (
                      <th key={head} className="px-3 py-2 text-right">{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.by_insured.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-4 text-center text-muted">Aucune dépense validée sur ces filtres.</td>
                    </tr>
                  ) : null}
                  {report.by_insured.map((row, index) => (
                    <tr key={`${row.family_code}-${row.patient}`} className="border-t border-border">
                      <td className="px-3 py-2 text-muted">{index + 1}</td>
                      <td className="px-3 py-2 font-mono text-xs font-semibold">{row.family_code}</td>
                      <td className="px-3 py-2">{row.patient}</td>
                      <td className="px-3 py-2">{row.insured}</td>
                      <StatsCells row={row} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
